import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { getPreguntasByImei } from '../../../domain/api/PreguntasApi';
import { mapPreguntasToFilters, getOpcionesEnCascada } from '../utils/filterMappers';

/**
 * Custom hook para cargar filtros dinámicos (Empresa, Área, Cargo)
 * basado en la estación EVBI seleccionada
 * 
 * Flujo:
 * 1. Si se seleccionan múltiples estaciones, usa solo la primera
 * 2. Obtiene el IMEI de la estación desde modulos[0].imei
 * 3. Consulta /v1/pregunta/evbi/{imei} para obtener preguntas/respuestas
 * 4. Mapea las respuestas a opciones de filtros
 * 5. Retorna opciones organizadas por tipo (companies, areas, positions)
 * 
 * @param {string|number|Array} stationId - ID de la estación seleccionada o array de IDs. Si es array, usa solo la primera.
 * @param {Array} estaciones - Array de estaciones con estructura completa (incluyendo modulos)
 * @returns {Object} { companies, areas, positions, loading, error }
 * @returns {Array} return.companies - Opciones de empresa [{id, label, value, siguiente}]
 * @returns {Array} return.areas - Opciones de área [{id, label, value, siguiente}]
 * @returns {Array} return.positions - Opciones de cargo [{id, label, value, siguiente}]
 * @returns {boolean} return.loading - Estado de carga
 * @returns {string|null} return.error - Mensaje de error si ocurre
 * 
 * @example
 * const { companies, areas, positions, loading, error } = useFiltrosDinamicos(
 *     filters.station,  // Puede ser un ID o un array de IDs
 *     estaciones
 * );
 */
export const useFiltrosDinamicos = (stationId, estaciones) => {
    const dispatch = useDispatch();
    const [companies, setCompanies] = useState([]);
    const [areas, setAreas] = useState([]);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [preguntas, setPreguntas] = useState([]);

    const fetchFiltros = useCallback(async () => {
        // Validar que hay estación seleccionada
        if (!stationId) {
            setCompanies([]);
            setAreas([]);
            setPositions([]);
            setError(null);
            return;
        }

        // Validar que hay estaciones disponibles
        if (!estaciones || !Array.isArray(estaciones) || estaciones.length === 0) {
            setCompanies([]);
            setAreas([]);
            setPositions([]);
            setError('No hay estaciones disponibles');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Si stationId es un array, usar solo la primera estación
            const firstStationId = Array.isArray(stationId) ? stationId[0] : stationId;

            // Buscar la estación seleccionada
            const station = estaciones.find(
                e => e && (e.id === firstStationId || e.hash === firstStationId)
            );

            if (!station) {
                setError('No se encontró la estación seleccionada');
                setCompanies([]);
                setAreas([]);
                setPositions([]);
                return;
            }

            // Validar que la estación tiene modulos y IMEI
            if (!station.modulos || !Array.isArray(station.modulos) || station.modulos.length === 0) {
                setError('La estación no tiene módulos configurados');
                setCompanies([]);
                setAreas([]);
                setPositions([]);
                return;
            }

            const imei = station.modulos[0].imei;
            if (!imei) {
                setError('La estación no tiene IMEI configurado');
                setCompanies([]);
                setAreas([]);
                setPositions([]);
                return;
            }

            // Consultar preguntas desde la API
            const preguntas = await getPreguntasByImei(dispatch, imei);

            if (!preguntas || preguntas.error) {
                setError('Error al cargar los filtros desde el servidor');
                setCompanies([]);
                setAreas([]);
                setPositions([]);
                return;
            }

            // Convertir objeto a array si es necesario
            let preguntasArray = preguntas;
            if (!Array.isArray(preguntas) && typeof preguntas === 'object') {
                preguntasArray = Object.values(preguntas);
            }

            // Validar que la respuesta es un array
            if (!Array.isArray(preguntasArray) || preguntasArray.length === 0) {
                setError('No se encontraron preguntas disponibles');
                setCompanies([]);
                setAreas([]);
                setPositions([]);
                return;
            }

            // Almacenar preguntas completas para uso en cascada
            setPreguntas(preguntasArray);

            // Mapear preguntas a opciones de filtros
            const { companies: mappedCompanies, areas: mappedAreas, positions: mappedPositions } = mapPreguntasToFilters(preguntasArray);

            setCompanies(mappedCompanies);
            setAreas(mappedAreas);
            setPositions(mappedPositions);
            setError(null);
        } catch (err) {
            console.error('Error loading dynamic filters:', err);
            setError('Error al cargar los filtros');
            setCompanies([]);
            setAreas([]);
            setPositions([]);
        } finally {
            setLoading(false);
        }
    }, [stationId, estaciones, dispatch]);

    // Función para cargar filtros en cascada basado en la respuesta seleccionada
    const cargarFiltroEnCascada = useCallback((siguienteOrden) => {
        if (!preguntas || preguntas.length === 0 || typeof siguienteOrden !== 'number') {
            return [];
        }
        return getOpcionesEnCascada(preguntas, siguienteOrden);
    }, [preguntas]);

    // Ejecutar cuando cambia la estación seleccionada
    useEffect(() => {
        fetchFiltros();
    }, [fetchFiltros]);

    return {
        companies,
        areas,
        positions,
        loading,
        error,
        cargarFiltroEnCascada,
        preguntas
    };
};

export default useFiltrosDinamicos;
